import { Link, useNavigate, useLocation } from "react-router-dom";
import { supabase, safeSignOut } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User as UserIcon, Image as ImageIcon, Shield, Scale, Book, Chrome, Sparkles, Menu, X, ChevronDown } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminMode } from "@/contexts/AdminModeContext";
import ResponsiveImage from "@/components/ResponsiveImage";
import { CreditDisplay } from "@/components/CreditDisplay";
import { DEFAULT_AVATAR_PICTURE } from "@/lib/defaultPictures";
import { GARU_DEMO_URL } from "@/lib/demoStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getCachedRoles, setCachedRoles, getCachedProfile, setCachedProfile, clearAllUserCache } from "@/lib/userDataCache";

/** Products dropdown (Lumoo nav pattern), the Garu product
 *  lines, each with a real product visual. Fit anchors into the
 *  landing section; Plus (the full suite) lands on pricing. Garu Studio
 *  lives at /studio/, linked from the footer. */
const GARU_PRODUCTS = [
  {
    id: "fit",
    name: "Garu Fit",
    desc: "Virtual try-on and AI sizing on your product page.",
    img: "/nav/look-thumb.jpg",
    anchor: "fit",
  },
  {
    id: "plus",
    name: "Garu Plus",
    desc: "Every size on their body, fabric and drape intact.",
    img: "/nav/plus-thumb.jpg",
    anchor: "plus",
  },
] as const;

/** Nav CTA, the B2B funnel's money action. Shopper sign-in stays
 *  reachable via the footer's "Sign In" link. */
const SHOPIFY_APP_STORE_URL =
  'https://apps.shopify.com/garu-virtual-try-on?utm_source=garu_nav&utm_medium=website&utm_campaign=shopify_launch';

/** Routes rendered by MerchantsSimple (`theme-luxe-landing`), match hero inset (`px-6 lg:px-24`). */
const MERCHANTS_LUXE_LANDING_PATHS = new Set([
  "/merchants",
  "/shopify-virtual-try-on",
  "/shopify-virtual-try-on-app",
  "/virtual-fitting-room-shopify",
  "/reduce-fashion-returns",
]);

/** Product (in-app) routes, when the user is *inside* the consumer
 *  product, the nav should surface app destinations (Closet, Billing)
 *  instead of the B2B marketing funnel (AI Sizing, Virtual Try-On,
 *  Pricing, FAQ). Same pattern Linear / Stripe / Notion use :
 *  marketing nav on the public site, product nav inside the app.
 *  Path-prefix matched so dynamic routes like /look/:id count as
 *  in-app surfaces too. */
const PRODUCT_ROUTE_PREFIXES: readonly string[] = [
  "/closet",
  "/look",
  "/chat",
  "/billing",
  "/profile",
  "/profile-settings",
  "/shared-look",
  "/onboard",
];

function isProductRoute(pathname: string): boolean {
  return PRODUCT_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

type HeaderProps = {
  /**
   * Ghost-over-media mode for routes whose first viewport is a
   * full-bleed video/image (the landing hero). The bar starts
   * transparent with white links floating over the media and
   * transitions to the standard solid-white surface as soon as the
   * user scrolls (Aiuta / Lumoo / SpreeAI nav pattern). Off by
   * default so every other route keeps the solid bar.
   */
  overlay?: boolean;
};

export const Header = ({ overlay = false }: HeaderProps = {}) => {
  // Single source of truth: get user from AuthContext
  const { user } = useAuth();
  const [userProfile, setUserProfile] = useState<{ display_name?: string; profile_picture_url?: string; bio?: string } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isContributor, setIsContributor] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // On overlay routes the bar logo stays hidden until the hero's
  // giant wordmark has morphed into its slot (~280px of scroll, just
  // before the morph's 320px dock so the swap overlaps smoothly).
  const [logoDocked, setLogoDocked] = useState(false);

  // Ghost nav only while the hero media is behind the bar. 24px of
  // scroll is enough to know the user has left the very top.
  useEffect(() => {
    if (!overlay) return;
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      setLogoDocked(window.scrollY > 305);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [overlay]);

  /* Warm the Products-menu thumbnails once the browser is idle.
     The menu only mounts its <img>s when it opens, so without this the
     first click always paid a full round trip and showed three empty
     grey squares. Three AVIFs at ~2-4 KB each, fetched at idle with low
     priority, so this cannot compete with the hero for bandwidth. */
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      if (cancelled) return;
      for (const p of GARU_PRODUCTS) {
        const dot = p.img.lastIndexOf('.');
        const img = new Image();
        img.fetchPriority = 'low';
        img.decoding = 'async';
        img.src = `${p.img.slice(0, dot)}-192.avif`;
      }
    };
    const ric = window.requestIdleCallback;
    const handle = ric ? ric(warm, { timeout: 3000 }) : window.setTimeout(warm, 2000);
    return () => {
      cancelled = true;
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(handle as number);
      else window.clearTimeout(handle as number);
    };
  }, []);

  // The mobile menu panel is a solid white sheet, force the solid
  // bar while it's open so the ghost bar never floats over it.
  const isGhost = overlay && !scrolled && !mobileMenuOpen;
  const navigate = useNavigate();
  const location = useLocation();
  const headerShellPadding = MERCHANTS_LUXE_LANDING_PATHS.has(location.pathname)
    ? "px-6 lg:px-24 py-4 md:py-6"
    : "px-4 md:px-6 lg:px-12 py-4 md:py-6";
  const { requireAuth } = useAuth();
  const { isAdminMode, toggleAdminMode } = useAdminMode();

  // React to user changes from AuthContext (single source of truth)
  useEffect(() => {
    if (user) {
      checkAdminStatus(user.id);
      loadUserProfile(user.id);
    } else {
      setIsAdmin(false);
      setIsContributor(false);
      setUserProfile(null);
    }
  }, [user]);

  // Listen for new users in realtime and show admin toast
  useEffect(() => {
    if (!isAdmin) return;
    const channel = supabase
      .channel('user_directory_realtime_header')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_directory' }, (payload) => {
        const row = payload.new as { email?: string; id: string };
        toast.message('New user joined', {
          description: row.email || row.id,
        });
      })
      .subscribe();
    return () => {
      try { supabase.removeChannel(channel); } catch (e) { /* noop */ }
    };
  }, [isAdmin]);

  // Listen for user model changes (when they upload their face picture)
  // Note: Only subscribe if we have a user to reduce connection overhead
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('user_models_realtime_header')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE
          schema: 'public',
          table: 'user_models',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          // Reload profile when model photo changes - skip cache to get fresh data
          loadUserProfile(user.id, true);
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch (e) { /* noop */ }
    };
  }, [user]);

  const checkAdminStatus = async (userId: string) => {
    // Check cache first to avoid DB call
    const cached = getCachedRoles(userId);
    if (cached) {
      setIsAdmin(cached.isAdmin);
      setIsContributor(cached.isContributor);
      return;
    }

    try {
			const { data, error } = await supabase
        .from('user_roles')
				.select('role')
				.eq('user_id', userId);
      
      if (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setIsContributor(false);
        return;
      }
      
			const roles = (data ?? []).map(r => (r as { role: string }).role);
			setIsAdmin(roles.includes('admin'));
			setIsContributor(roles.includes('contributor'));
      
      // Cache the result
      setCachedRoles(userId, roles);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setIsContributor(false);
    }
  };

  const loadUserProfile = async (userId: string, skipCache = false) => {
    // Check cache first to avoid DB calls (unless explicitly skipped, e.g., after profile update)
    if (!skipCache) {
      const cached = getCachedProfile(userId);
      if (cached) {
        setUserProfile({
          display_name: cached.display_name,
          profile_picture_url: cached.profile_picture_url,
          bio: cached.bio,
        });
        return;
      }
    }

    try {
      // Fetch user profile
      const { data: profileData, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user profile:', error);
        return;
      }

      // Fetch user model (closet face picture)
      const { data: modelData } = await supabase
        .from("user_models")
        .select("thumbnail_url")
        .eq("user_id", userId)
        .maybeSingle();

      let profile: { display_name?: string; profile_picture_url?: string; bio?: string } | null = null;

      if (profileData) {
        profile = {
          display_name: profileData.display_name,
          // Prefer model photo (from closet) over regular profile picture
          profile_picture_url: modelData?.thumbnail_url || profileData.profile_picture_url,
          bio: profileData.bio
        };
      } else if (modelData?.thumbnail_url) {
        // If no profile exists but model photo exists, use it
        profile = {
          profile_picture_url: modelData.thumbnail_url
        };
      }

      setUserProfile(profile);
      
      // Cache the result
      if (profile) {
        setCachedProfile(userId, profile);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUserProfile(null);
    }
  };

  const CHROME_EXTENSION_URL =
    "https://chromewebstore.google.com/detail/garu-360%C2%B0-virtual-try-ons/ajihemmafmpegokcdplieniipodnpbjo";
  // Routes that render the marketing landing page (MerchantsSimple).
  // In-page anchors like #ai-sizing only make sense on these, from
  // anywhere else we navigate to / with the hash and let the landing
  // page's scroll-on-hash effect take over.
  const LANDING_ROUTES = new Set([
    "/",
    "/merchants",
    "/shopify-virtual-try-on",
    "/shopify-virtual-try-on-app",
    "/virtual-fitting-room-shopify",
    "/reduce-fashion-returns",
  ]);

  const scrollToSection = (id: string) => {
    if (LANDING_ROUTES.has(location.pathname)) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Mirror the hash in the URL so the section is shareable
        // without forcing a re-render via React Router.
        try {
          window.history.replaceState(null, "", `${location.pathname}#${id}`);
        } catch (e) { /* noop */ }
        return;
      }
    }
    navigate(`/#${id}`);
  };

  const handleSignOut = async () => {
    try {
      console.log('[Auth] Starting sign out...');
      toast.loading("Signing out...", { id: 'signing-out' });
      
      // Clear user data cache immediately
      clearAllUserCache();
      
      // Hard UI fallback in case everything hangs (network, blockers, etc.)
      let completed = false;
      const uiFallback = setTimeout(() => {
        if (completed) return;
        console.warn('[Auth] Forcing local sign-out UI after timeout');
        toast.message("Signed out locally due to slow network", { id: 'signing-out' });
        navigate("/", { replace: true });
        setTimeout(() => window.location.reload(), 300);
      }, 6500);
      
      // Use safeSignOut which has timeout and fallback logic
      await safeSignOut();
      completed = true;
      clearTimeout(uiFallback);
      
      console.log('[Auth] ✅ Sign out successful');
      toast.success("Signed out successfully", { id: 'signing-out' });
      
      // Force navigate to home
      navigate("/", { replace: true });
      
      // Force a hard refresh of the page to clear any stuck state
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('[Auth] ❌ Error signing out:', error);
      toast.error("Error signing out. Refreshing page...", { id: 'signing-out' });
      
      // Even if sign out failed, try to clear local state and refresh
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    }
  };

  // Chat features removed for simplified MVP

  return (
    <>
      {/* WCAG 2.4.1 Bypass Blocks. Visually hidden until focused, so the
          first Tab on any page offers a jump past the nav instead of
          walking the whole menu. Targets <main id="main-content">. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-neutral-900 focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-neutral-900"
      >
        Skip to main content
      </a>
    <header
      className={`w-full fixed top-0 left-0 right-0 z-[60] transition-colors duration-300 ${
        isGhost
          ? 'bg-transparent border-b border-transparent'
          : overlay
            ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200/60'
            : 'bg-white border-b border-neutral-200/60'
      }`}
    >
      {/*
        Solid white surface so content cleanly slides UNDER the nav as the
        user scrolls, never through it. The previous gradient faded to
        transparent at its bottom edge which let body content bleed into
        the header on scroll (looks like the nav is "glitching" or covering
        the page). A solid surface plus a hairline bottom border is the
        Stripe / Linear / Aiuta pattern, premium, trustworthy, no glitch.
      */}
      <div className={headerShellPadding}>
      {/* Desktop navbar - B2B focused */}
      <div className="hidden md:flex relative w-full items-center justify-between max-w-[1440px] mx-auto">
        {/* Left side - Logo */}
        <Link 
          to="/" 
          className="flex-shrink-0" 
          onClick={(e) => {
            e.preventDefault();
            if (location.pathname === "/") {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate("/");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }
          }}
        >
          {/* On overlay routes the bar logo stays hidden until the
              hero's giant wordmark has morphed into this slot, the
              morph element targets [data-garu-logo]. */}
          <span data-garu-logo className={`text-4xl font-bold tracking-[0.08em] transition-opacity duration-500 ${overlay && !logoDocked ? 'opacity-0' : 'opacity-100'} ${isGhost ? 'text-white' : 'text-logo-gradient'}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>garu</span>
        </Link>
        
        {/* Center nav : two distinct sets based on route context.
              · Marketing routes (landing, merchants, pricing, faq,
                shoppers, about, contact, …) → B2B funnel nav :
                AI Sizing, Virtual Try-On, Pricing, FAQ.
              · Product routes (/closet, /look/:id, /chat,
                /billing, /profile, …) → in-app nav : Closet.
                The marketing items would be pure noise inside
                the product (Linear / Stripe / Notion pattern). */}
        {isProductRoute(location.pathname) ? (
          <nav className="flex items-center gap-1">
            <Link
              to="/closet"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith("/closet")
                  ? "text-neutral-900"
                  : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              Closet
            </Link>
          </nav>
        ) : (
          <nav className="flex items-center gap-1">
            {/* Products dropdown, click to open, backdrop click to
                close. The panel is always a white sheet, even in
                ghost mode over the hero film. */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setProductsOpen((o) => !o)}
                aria-expanded={productsOpen}
                aria-haspopup="menu"
                className={`px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
              >
                Products
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`}
                  strokeWidth={2}
                />
              </button>
              {productsOpen && (
                <>
                  <button
                    type="button"
                    aria-hidden
                    tabIndex={-1}
                    onClick={() => setProductsOpen(false)}
                    className="fixed inset-0 cursor-default"
                  />
                  <div
                    role="menu"
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[420px] bg-white rounded-2xl p-2 border border-neutral-100 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)]"
                  >
                    {GARU_PRODUCTS.map((p) => {
                      const row = (
                        <>
                          <span className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-neutral-100">
                            {/* 96 CSS px. These were 480x480 JPEGs (up to 55 KB)
                                scaled down by the browser, and `loading="lazy"`
                                on an element that only mounts when the menu
                                opens meant the fetch could not start until the
                                click. Now 96/192 AVIF (2-4 KB) and eager, with
                                the file already warmed by prefetchNavThumbs. */}
                            <ResponsiveImage
                              src={p.img}
                              widths={[96, 192]}
                              sizes="96px"
                              width={480}
                              height={480}
                              alt=""
                              loading="eager"
                              fetchPriority="high"
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-neutral-900">{p.name}</span>
                            <span className="block text-[12px] leading-snug text-neutral-500 mt-0.5">{p.desc}</span>
                          </span>
                        </>
                      );
                      return 'to' in p ? (
                        <Link
                          key={p.id}
                          to={p.to}
                          role="menuitem"
                          onClick={() => setProductsOpen(false)}
                          className="flex items-center gap-3.5 p-2.5 rounded-xl hover:bg-neutral-50 transition-colors text-left no-underline"
                        >
                          {row}
                        </Link>
                      ) : (
                        <button
                          key={p.id}
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setProductsOpen(false);
                            scrollToSection(p.anchor);
                          }}
                          className="w-full flex items-center gap-3.5 p-2.5 rounded-xl hover:bg-neutral-50 transition-colors text-left"
                        >
                          {row}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {/* Static landing page (apps/landing/case-studies), so a plain
                anchor rather than a router Link. */}
            <a
              href="/case-studies/"
              className={`px-4 py-2 text-sm font-medium no-underline transition-colors ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
            >
              Case studies
            </a>
            <a
              href="/agencies/"
              className={`px-4 py-2 text-sm font-medium no-underline transition-colors ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
            >
              Agencies
            </a>
            {/* First-party destination on its own subdomain, so a plain
                anchor rather than a router Link — and in place, not a new
                tab: it reads as a section of the site, which is what the
                prose links to demo.garu.ai elsewhere already assume. */}
            <a
              href={GARU_DEMO_URL}
              className={`px-4 py-2 text-sm font-medium no-underline transition-colors ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
            >
              Demo
            </a>
            <Link
              to="/pricing"
              className={`px-4 py-2 text-sm font-medium transition-colors ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className={`px-4 py-2 text-sm font-medium transition-colors ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
            >
              Blog
            </Link>
            <Link
              to="/faq"
              className={`px-4 py-2 text-sm font-medium transition-colors ${isGhost ? 'text-white/80 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'}`}
            >
              FAQ
            </Link>
          </nav>
        )}
        
        {/* Right side - CTA + Login */}
        <div className="flex items-center gap-3">
          {user && <CreditDisplay />}
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-10 h-10 rounded-full border border-gray-200 bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-gray-50 transition-colors overflow-hidden">
                  <Avatar className="h-full w-full">
                    <AvatarImage
                      src={userProfile?.profile_picture_url || DEFAULT_AVATAR_PICTURE}
                      alt={userProfile?.display_name || user.email || "Profile"}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-gray-100 text-gray-600 text-sm font-medium">
                      {userProfile?.display_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="bottom" className={`${isAdmin ? 'w-64 max-h-[80vh] overflow-y-auto' : 'w-52'} bg-white border border-slate-200 shadow-lg z-[70] rounded-xl`}>
                <div className="p-2 space-y-1">
                  {/* Simple menu for regular users */}
                  {!isAdmin && !isContributor && (
                    <>
                      <DropdownMenuItem 
                        onClick={() => navigate("/billing")} 
                        className="cursor-pointer min-h-[44px] touch-manipulation rounded-lg hover:bg-slate-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <Sparkles className="h-4 w-4 text-slate-600" />
                          <span className="font-medium text-slate-700">Billing & Credits</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="my-2 bg-slate-200" />
                      <DropdownMenuItem 
                        onClick={handleSignOut} 
                        className="cursor-pointer min-h-[44px] touch-manipulation rounded-lg hover:bg-red-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <UserIcon className="h-4 w-4 text-red-500" />
                          <span className="font-medium text-red-500">Sign Out</span>
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}
                  
                  {/* Full menu for admins and contributors */}
                  {(isAdmin || isContributor) && (
                    <>
                      {/* Account Section */}
                      <div className="px-3 py-1.5">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                          Account
                        </p>
                      </div>
                      <DropdownMenuItem 
                        onClick={() => navigate("/profile-settings")} 
                        className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <UserIcon className="h-4 w-4 text-slate-600" />
                          <span className="font-medium text-slate-700">Profile Settings</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate("/billing")} 
                        className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <Sparkles className="h-4 w-4 text-slate-600" />
                          <span className="font-medium text-slate-700">Billing & Credits</span>
                        </div>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.preventDefault();
                              toggleAdminMode();
                            }} 
                            className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                          >
                            <div className="flex items-center gap-3 w-full justify-between">
                              <div className="flex items-center gap-3">
                                <Shield className={`h-4 w-4 ${isAdminMode ? 'text-green-600' : 'text-slate-400'}`} />
                                <span className="font-medium text-slate-700">Admin Mode</span>
                              </div>
                              <div className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isAdminMode ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                {isAdminMode ? 'ON' : 'OFF'}
                              </div>
                            </div>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => navigate("/docs")} 
                            className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                          >
                            <div className="flex items-center gap-3 w-full">
                              <Book className="h-4 w-4 text-gray-800" />
                              <span className="font-medium text-slate-700">Documentation</span>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {/* Content Section - for contributors and admins */}
                      <DropdownMenuSeparator className="my-2 bg-slate-200" />
                      <div className="px-3 py-1.5">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                          Content
                        </p>
                      </div>
                      <DropdownMenuItem 
                        onClick={() => navigate("/celebrity-outfits-admin")} 
                        className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <ImageIcon className="h-4 w-4 text-slate-600" />
                          <span className="font-medium text-slate-700">Celebrity Outfits</span>
                        </div>
                      </DropdownMenuItem>
                      
                      {/* Admin Section - only for admins */}
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator className="my-2 bg-slate-200" />
                          <div className="px-3 py-1.5">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                              Admin
                            </p>
                          </div>
                          <DropdownMenuItem 
                            onClick={() => navigate("/admin-users")} 
                            className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                          >
                            <div className="flex items-center gap-3 w-full">
                              <UserIcon className="h-4 w-4 text-slate-600" />
                              <span className="font-medium text-slate-700">Users</span>
                            </div>
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {/* System Section */}
                      <DropdownMenuSeparator className="my-2 bg-slate-200" />
                      <div className="px-3 py-1.5">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                          System
                        </p>
                      </div>
                      <DropdownMenuItem 
                        onClick={() => navigate("/legal")} 
                        className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-slate-50 transition-colors duration-200"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <Scale className="h-4 w-4 text-slate-600" />
                          <span className="font-medium text-slate-700">Legal Information</span>
                        </div>
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator className="my-3 bg-slate-200" />
                      <DropdownMenuItem 
                        onClick={handleSignOut} 
                        className="cursor-pointer min-h-[40px] touch-manipulation rounded-md hover:bg-red-50 transition-colors duration-200 group"
                      >
                        <div className="flex items-center gap-3 w-full">
                          <UserIcon className="h-4 w-4 text-red-600" />
                          <span className="font-medium text-red-600">Sign Out</span>
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <a
              href={SHOPIFY_APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${isGhost ? 'garu-btn-cta-on-dark' : 'garu-btn-cta'} inline-flex items-center justify-center !normal-case !tracking-normal !text-sm no-underline`}
            >
              Install on Shopify
            </a>
          )}
        </div>
      </div>

      {/* Mobile navbar : logo on the left, hamburger on the right.
          The hamburger reveals the full-screen panel below, which
          carries the same nav links the desktop bar shows. */}
      <div className="flex md:hidden w-full items-center justify-between">
        <Link
          to="/"
          className="flex-shrink-0"
          onClick={(e) => {
            e.preventDefault();
            setMobileMenuOpen(false);
            if (location.pathname === "/") {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate("/");
              setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
            }
          }}
        >
          {/* Ghost mode (over the hero film) shows the wordmark in white —
              it used to be opacity-0 there, which left the mobile hero with
              no brand mark at the top at all. Once the visitor scrolls the
              bar goes solid and the gradient version takes over. */}
          <span className={`text-3xl font-bold tracking-[0.08em] transition-colors duration-500 ${isGhost ? 'text-white' : 'text-logo-gradient'}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>garu</span>
        </Link>

        <div className="flex items-center gap-2">
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full border border-gray-200 bg-white/80 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                  <Avatar className="h-full w-full">
                    <AvatarImage
                      src={userProfile?.profile_picture_url || DEFAULT_AVATAR_PICTURE}
                      alt={userProfile?.display_name || user.email || "Profile"}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-gray-100 text-gray-600 text-xs font-medium">
                      {userProfile?.display_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white border border-slate-200 shadow-lg z-[70] rounded-xl">
                {!isAdmin && !isContributor && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/billing")} className="cursor-pointer">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Billing & Credits
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-500">
                      <UserIcon className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </>
                )}

                {(isAdmin || isContributor) && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/profile-settings")} className="cursor-pointer">
                      <UserIcon className="h-4 w-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/billing")} className="cursor-pointer">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Billing & Credits
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate("/admin-users")} className="cursor-pointer">
                        <Shield className="h-4 w-4 mr-2" />
                        Manage Users
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-red-600">
                      <UserIcon className="h-4 w-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Hamburger : toggles the full-screen mobile panel. */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            className={`w-10 h-10 rounded-full flex items-center justify-center active:scale-95 transition-transform ${
              isGhost
                ? 'bg-white/15 border border-white/30 text-white backdrop-blur-sm'
                : 'bg-white/80 border border-neutral-200 text-neutral-900'
            }`}
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5" strokeWidth={1.75} />
            ) : (
              <Menu className="w-5 h-5" strokeWidth={1.75} />
            )}
          </button>
        </div>
      </div>
      </div>

      {/* Mobile menu panel : full-bleed, opens below the header bar.
          Same split as the desktop nav, product routes get the
          in-app destinations, marketing routes get the B2B funnel
          links. Closes on tap so the user lands on the route. */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute left-0 right-0 top-full bg-white border-t border-neutral-100 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.12)]">
          <nav className="flex flex-col px-6 py-4">
            {isProductRoute(location.pathname) ? (
              <>
                <Link
                  to="/closet"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 border-b border-neutral-100"
                >
                  Closet
                </Link>
                <Link
                  to="/billing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 border-b border-neutral-100"
                >
                  Billing &amp; Credits
                </Link>
              </>
            ) : (
              <>
                {/* Products group, same three entries as the desktop
                    dropdown, with their product visuals. */}
                <p className="pt-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400">
                  Products
                </p>
                {GARU_PRODUCTS.map((p) =>
                  'to' in p ? (
                    <Link
                      key={p.id}
                      to={p.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-3 flex items-center gap-3.5 border-b border-neutral-100 no-underline"
                    >
                      <span className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-neutral-100">
                        <img src={p.img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
                      </span>
                      <span>
                        <span className="block text-[16px] font-medium text-neutral-900">{p.name}</span>
                        <span className="block text-[12px] text-neutral-500">{p.desc}</span>
                      </span>
                    </Link>
                  ) : (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        scrollToSection(p.anchor);
                      }}
                      className="py-3 flex items-center gap-3.5 text-left border-b border-neutral-100 w-full"
                    >
                      <span className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-neutral-100">
                        <img src={p.img} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover object-top" />
                      </span>
                      <span>
                        <span className="block text-[16px] font-medium text-neutral-900">{p.name}</span>
                        <span className="block text-[12px] text-neutral-500">{p.desc}</span>
                      </span>
                    </button>
                  )
                )}
                <a
                  href="/case-studies/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 no-underline border-b border-neutral-100"
                >
                  Case studies
                </a>
                <a
                  href="/agencies/"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 no-underline border-b border-neutral-100"
                >
                  Agencies
                </a>
                <a
                  href={GARU_DEMO_URL}
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 no-underline border-b border-neutral-100"
                >
                  Demo
                </a>
                <Link
                  to="/pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 border-b border-neutral-100"
                >
                  Pricing
                </Link>
                <Link
                  to="/blog"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 border-b border-neutral-100"
                >
                  Blog
                </Link>
                <Link
                  to="/faq"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-lg font-medium text-neutral-900 border-b border-neutral-100"
                >
                  FAQ
                </Link>

                {/* Secondary : shopper-side + Demo Store links live
                    below the primary brand funnel on marketing routes
                    only, these are conversion entry points, not
                    in-app destinations. */}
                <a
                  href={CHROME_EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="pt-5 pb-2 text-sm font-medium text-neutral-500"
                >
                  Chrome Extension
                </a>
                <a
                  href={GARU_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-2 text-sm font-medium text-neutral-500"
                >
                  Garu on a live Shopify store
                </a>
              </>
            )}

            {!user && (
              <a
                href={SHOPIFY_APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="garu-btn-cta mt-5 mb-3 inline-flex w-full items-center justify-center !normal-case !tracking-normal !text-sm no-underline"
              >
                Install on Shopify
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
    </>
  );
};